import React from 'react';
import Switch from 'react-switch';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';

import TextBase from '../text';
import CheckIcon from '@mui/icons-material/Check';
import { convertPxToRem, translate } from '../../helper';
import { Colors } from '../../themes/Colors';
import SaveIcon from '../../assets/image/svg/save-25.svg';

import MonthlyIcon from '../../assets/image/svg/monthly_icon.svg';
import YearlyIcon from '../../assets/image/svg/yearly_icon.svg';
import ButtonGradient from '../button-gradient';
import { formatCurrency } from '../../utils/CommonFunction';
import { toUpper } from 'lodash';

dayjs.extend(LocalizedFormat);

export const SubscriptionCard = ({
  title = '',
  subTitle = '',
  subscription = {},
  trial = [],
  onChange = () => { },
  isYearly = true,
  terms = [],
  pricelist = [],
  childAccount = 0,
  labelButton = 'PURCHASE',
  onPurchase = () => { },
  updatePaymentInfo = () => { },
  payForAllChildAcount = true,
  showEmpty = true,
  style = {},
  subContent = <></>,
  variant = 'dark',
  badgeText = '',
  highlight = false,
  hideCTA = false,
  selectable = false,
  onSelect = () => { },
  includedTitle = 'Orders & Messages Included',
  includedItems = [],
  additionalTitle = 'Additional Orders & Messages Cost',
  additionalItems = [],
  planColor = '#3B82F6',
  planKey = '',
}) => {
  const hexToRgba = (hex, alpha) => {
    const normalizedHex = (hex || '').replace('#', '');
    if (normalizedHex.length !== 6) return `rgba(59, 130, 246, ${alpha})`;

    const red = parseInt(normalizedHex.slice(0, 2), 16);
    const green = parseInt(normalizedHex.slice(2, 4), 16);
    const blue = parseInt(normalizedHex.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  };

  const trialObj = trial?.find((item) => item?.subscription_price === subscription?.id);
  const period = subscription?.recurring_interval;
  const discountPercent = Number(subscription?.discount ?? 0);
  const originalPrice = Number(subscription?.price ?? 0);
  const discountRate = Math.min(Math.max(discountPercent, 0), 100) / 100;
  const discountedPrice = originalPrice * (1 - discountRate);
  const savedAmount = Math.max(originalPrice - discountedPrice, 0);

  const handleSelection = () => {
    const selectedPrice = subscription?.price || 0;
    const selectedPriceId = pricelist?.[0]?.id || null;
    updatePaymentInfo(selectedPrice, selectedPriceId);
  };

  const discountText = discountPercent.toFixed(2).replace(/\.00$/, '');

  const Bullet = ({ children }) => {
    const str = typeof children === 'string' ? children : '';
    const isSpacer = !str || str === '\u00A0' || str.trim() === '' || str === (translate('negotiatedBulkRatesSpacer') || '');
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'calc(var(--sp-md2) * 1.0526315)',
          marginBottom: 'calc(var(--sp-md2) * 1.0526315)',
          visibility: isSpacer ? 'hidden' : 'visible'
        }}
      >
        <div
          style={{
            width: 'calc(var(--fs-xs) * 2.10526315)',
            height: 'calc(var(--fs-xs) * 2.10526315)',
            borderRadius: '50%',
            background: planColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
            minWidth: 'calc(var(--fs-xs) * 2.10526315)',
            minHeight: 'calc(var(--fs-xs) * 2.10526315)',
            boxSizing: 'border-box'
          }}
          aria-label={isSpacer ? undefined : (translate('includedItemChecked') || 'Included')}
        >
          {!isSpacer && <CheckIcon style={{ color: Colors.white, fontSize: 'calc(var(--fs-xs) * 1.6842105)' }} />}
        </div>
        <div style={{ color: 'rgba(71, 72, 76, 1)', fontSize: 'calc(var(--fs-xs) * 1.578947)', fontWeight: 400 }}>{children}</div>
      </div>
    );
  };

  const renderDarkCard = () => (
    <div
      style={{
        minHeight: '200px',
        backgroundColor: '#000000',
        border: '6px solid #FBB516',
        borderRadius: 'calc(var(--sp-md2)*1.2631579)',
        padding: 32,
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        cursor: selectable ? 'pointer' : 'default'
      }}
      onClick={selectable ? onSelect : undefined}
    >
      <img src={isYearly ? YearlyIcon : MonthlyIcon} style={{ height: 60, width: 60, marginBottom: 16 }} />
      <TextBase style={{ textAlign: 'center', marginBottom: 16 }} fontWeight={700} size={'calc(var(--fs-xl)*1.3157894)'} color={Colors.white}>
        {title}
      </TextBase>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 8 }}>
        <TextBase fontWeight={600} size={convertPxToRem(24)} color={Colors.primary}>$</TextBase>
        <TextBase fontWeight={600} size={convertPxToRem(26)} color={Colors.white}>
          {childAccount ? formatCurrency(Number(subscription?.price) * childAccount) : formatCurrency(subscription?.price)}
        </TextBase>
        <TextBase fontWeight={600} size={convertPxToRem(16)} color={Colors.white}>USD</TextBase>
        <div style={{ fontWeight: 500, fontSize: convertPxToRem(16), color: Colors.white55 }}>/{period}</div>
      </div>
      {!!subTitle && (
        <TextBase style={{ textAlign: 'center', marginBottom: 'calc(var(--sp-md2)*1.2631579)' }} fontWeight={400} size={convertPxToRem(24)} color={Colors.primary}>
          {subTitle}
        </TextBase>
      )}
    </div>
  );

  const renderModernCard = () => (
    <div
      style={{
        minHeight: 'calc(var(--fs-hero) * 10.736842)',
        background: highlight
          ? `linear-gradient(180deg, ${hexToRgba(planColor, 0.18)} 0%, ${hexToRgba(planColor, 0.08)} 34%, #FFFFFF 100%)`
          : Colors.white,
        borderStyle: 'solid',
        borderColor: Colors.gray300,
        borderWidth: 'calc(var(--fs-xs)*0.0943396)',
        borderTopColor: planColor,
        borderTopWidth: 'calc(var(--fs-xs)*0.5660377)',
        borderRadius: 'calc(var(--sp-md2) * 1.6842105)',
        padding: 'calc(var(--sp-lg) * 1.2105263) calc(var(--sp-lg) * 1)',
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
        alignItems: 'stretch',
        position: 'relative',
        outline: highlight ? `calc(var(--fs-xs)*0.1886792) solid ${planColor}` : Colors.gray300,
        outlineOffset: 'calc(var(--fs-xs)*-0.1886792)',
        boxShadow: highlight 
          ? `0 16px 34px ${hexToRgba(planColor, 0.24)}, 0 10px 24px rgba(0,0,0,0.16), inset 0 1px 0 ${hexToRgba(planColor, 0.22)}` 
          : '0 8px 20px rgba(0,0,0,0.08)',
        cursor: selectable ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        transform: highlight ? 'translateY(calc(var(--sp-xxs)*-1))' : 'none'
      }}
      onClick={selectable ? onSelect : undefined}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <TextBase style={{ marginBottom: 'calc(var(--sp-md2)*1.2631579)' }} fontWeight={700} size={'calc(var(--fs-xl)*1.3157894)'} color={Colors.black}>{title}</TextBase>
        {highlight && (
          <div style={{ position: 'relative', width: 'calc(var(--fs-xs)*2.105263)', height: 'calc(var(--fs-xs)*2.105263)', borderRadius: 'calc(var(--sp-md2)*1.8947368)', border: `calc(var(--fs-xs)*0.1886792) solid ${planColor}`, boxSizing: 'border-box' }}>
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'calc(var(--fs-xs)*1.1578947)',
                height: 'calc(var(--fs-xs)*1.1578947)',
                borderRadius: 'calc(var(--sp-md2)*1.8947368)',
                background: planColor,
              }}
            />
          </div>
        )}
      </div>
      {!!subTitle && (
        <TextBase style={{ marginBottom: 'calc(var(--sp-md2)*1.6842105)' }} fontWeight={400} size={'calc(var(--fs-xs)*1.4736842)'} color={'rgba(101, 101, 101, 1)'}>
          {subTitle}
        </TextBase>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'calc(var(--sp-xxs)*0.5263158)', marginBottom: 'calc(var(--sp-xxs)*1.0526316)' }}>
        {isYearly && discountPercent > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'calc(var(--sp-md2)*0.5263158)'}}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'calc(var(--sp-xxs)*1.0526315)' }}>
              <TextBase
                fontWeight={500}
                size={'calc(var(--fs-xs)*1.368421)'}
                color={'rgba(145, 145, 145, 1)'}
                style={{ textDecoration: 'line-through' }}
              >
                {formatCurrency(originalPrice)}
              </TextBase>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: 'calc(var(--sp-xxs)*0.5263158) calc(var(--sp-xxs)*1.0526315)',
                  borderRadius: 'calc(var(--fs-hero)*23.56132075)',
                  background: `${planColor}20`,
                  color: planColor,
                  fontSize: 'calc(var(--fs-xs)*1.315789)',
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {`${discountText}% off`}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'calc(var(--sp-xxs)*1.578947)' }}>
              <TextBase fontWeight={700} size={'calc(var(--fs-xl)*1.3157894)'} color={Colors.black}>
                {formatCurrency(discountedPrice)}
              </TextBase>
              <div style={{ fontWeight: 500, fontSize:'calc(var(--fs-xs)*1.6842105)', color: 'rgba(145, 145, 145, 1)' }}>/{period}</div>
            </div>
            <TextBase
              fontWeight={600}
              size={'calc(var(--fs-xs)*1.368421)'}
              color={'#0F766E'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: 'calc(var(--sp-xxs) * 0.35) calc(var(--sp-xxs) * 0.8)',
                borderRadius: 'calc(var(--fs-hero)*23.56132075)',
                background: 'rgba(15, 118, 110, 0.10)',
              }}
            >
              {`You save ${formatCurrency(savedAmount)} annually`}
            </TextBase>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'calc(var(--sp-xxs)*1.578947)' }}>
            <TextBase fontWeight={600} size={'calc(var(--fs-xl)*1.3157894)'} color={Colors.black}>
              {formatCurrency(subscription?.price)}
            </TextBase>
            <div style={{ fontWeight: 500, fontSize:'calc(var(--fs-xs)*1.6842105)', color: 'rgba(145, 145, 145, 1)' }}>/{period}</div>
          </div>
        )}
      </div>

      <div style={{ height: 1, background: Colors.gray300, margin: 'calc(var(--sp-lg)*1.05263) calc(var(--sp-lg) * -1)' }} />

      {includedItems?.length > 0 && (
        <div style={{ marginBottom: 'calc(var(--sp-md2)*1.8947368)' }}>
          <TextBase style={{ marginBottom: 'calc(var(--sp-lg)*1.05263158)' }} fontWeight={500} size={'calc(var(--fs-xs)*1.6842105)'} color={Colors.black}>
            {includedTitle}
          </TextBase>
          <div>
            {includedItems.map((item, idx) => (
              <Bullet key={`inc-${idx}`}>{item}</Bullet>
            ))}
          </div>
        </div>
      )}

      {additionalItems?.length > 0 && (
        <div>
          <TextBase style={{ marginBottom: 'calc(var(--sp-md2)*2.105263)' }} fontWeight={500} size={'calc(var(--fs-xs)*1.6842105)'} color={Colors.black}>
            {additionalTitle}
          </TextBase>
          <div style={{ marginTop: 'calc(var(--sp-md2)*1.2631579)' }}>
            {additionalItems.map((item, idx) => (
              <Bullet key={`add-${idx}`}>{item}</Bullet>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ width: '70%', maxWidth: "calc(var(--fs-hero)*18.86792)", ...style }}>
      {!showEmpty && <div style={{ display: 'flex', marginBottom: '20px', justifyContent: 'center' }}>
        <TextBase
          style={{ opacity: isYearly ? 0.5 : 1 }}
          fontWeight={500}
          size={convertPxToRem(14)}
          color={Colors.black}>
          payMonthly
        </TextBase>
        <div className="react-switch" style={{ margin: '0px 20px' }}>
          <Switch
            checked={isYearly}
            onColor={Colors.primary}
            offColor="#E5E7EB"
            checkedHandleIcon={<div />}
            inputMode="none"
            onChange={onChange}
            uncheckedIcon={<div />}
            checkedIcon={<div />}
            disabled={pricelist?.length <= 1}
          />
        </div>

        <div style={{ position: 'relative' }}>
          <TextBase
            style={{ opacity: isYearly ? 1 : 0.5 }}
            fontWeight={500}
            size={convertPxToRem(14)}
            color={Colors.black}>
            payYearly
          </TextBase>

          {isYearly && (
            <div
              style={{
                height: '100px',
                width: '100px',
                position: 'absolute',
                top: '-60px',
                right: '-100px',
                // backgroundColor: "yellow"
              }}
            >
              <div
                style={{
                  position: "relative",
                  height: '100px',
                  width: '100px',
                  // backgroundColor: "red"
                }}
              >
                <span
                  style={{
                    display: "flex",
                    fontSize: "12px",
                    position: "absolute",
                    bottom: "65px",
                    transform: "rotate(-12deg)",
                    left: "2px",
                    fontWeight: 500
                  }}
                >
                  {translate('saveMoney')}&nbsp;{Number(subscription?.discount)?.toFixed(2)?.replace(/\.00$/, '')}%
                </span>
                <img
                  src={SaveIcon}
                  style={{
                    height: '70px',
                    width: '70px',
                    position: "absolute",
                    bottom: '25px'
                  }}
                />
              </div>
            </div>
            
          )}
        </div>
      </div>}

      {trialObj?.is_trial_active ? (
        <div
          style={{
            display: 'flex',
            marginBottom: '20px',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
          <TextBase
            style={{ opacity: 1 }}
            fontWeight={600}
            size={convertPxToRem(24)}
            color={Colors.black}>
            getFreeTrial
          </TextBase>
          <TextBase
            style={{ opacity: 1, textAlign: 'center' }}
            fontWeight={400}
            size={convertPxToRem(16)}
            color={Colors.black}>
            lockCodeStatement{'\n '}
            {dayjs()
              .add(trialObj?.trial_period_days || 0, 'day')
              .format('MMM D, YYYY')}
          </TextBase>
        </div>
      ) : null}

      {variant === 'modern' ? renderModernCard() : renderDarkCard()}
      {!hideCTA && (
        <ButtonGradient
          style={{
            marginTop: "50px",
            width: "100%",
            background: `linear-gradient(135deg, ${planColor}E6 0%, ${planColor} 100%)`
          }}
          title={toUpper(translate(labelButton))}
          disabled={!payForAllChildAcount}
          onClick={() => {
            handleSelection();
            onPurchase();
          }}
        />
      )}
    </div>
  );
};
